import { useEffect, useState } from "react";

import calculateNewPollingInterval from "@/view/utils/calculateNewPollingInterval";

const ALLOWED_TOTAL_DURATION = 3600000; // 60 minutes
const DURATION_BEFORE_INTERVAL_INCREASE = 180000; // 3 minutes
const INITIAL_TIMEOUT_INTERVAL = 3500; // 3.5 seconds

const usePollingForPollResults = ({
  allowedTotalDuration = ALLOWED_TOTAL_DURATION,
  canSeeResults,
  durationBeforeIntervalIncrease = DURATION_BEFORE_INTERVAL_INCREASE,
  initialInterval = INITIAL_TIMEOUT_INTERVAL,
  isDraftAssignment,
  isInView,
  getPoll,
  pollId,
  setAssignedPoll,
  totalPollAssignees,
  userProperties,
  isUsingSocketConnection,
}) => {
  const isStudent = userProperties?.isStudent;
  const isTeacher = userProperties?.isTeacher;

  const pendingTimeouts = [];
  let totalTimeSinceLastResult = 0;
  let totalTimeSinceInitialRequest = 0;
  let totalVotes = 0;

  const [documentIsVisibile, setDocumentIsVisibile] = useState(true);

  const recursiveTimeout = (interval, totalTimeSinceResultIncrease = 0) => {
    const pollIsCompleted = totalPollAssignees === totalVotes;
    // Stop polling either when:
    // - total time since initial request is over allowed duration
    // - poll is complete
    const shouldStopPolling =
      !documentIsVisibile ||
      !isInView ||
      totalTimeSinceInitialRequest >= allowedTotalDuration ||
      (isTeacher && pollIsCompleted) ||
      isUsingSocketConnection;
    if (shouldStopPolling) return;

    const timeout = setTimeout(async () => {
      const { data: pollData } = await getPoll({ pollId });
      const currentTotalVotes = pollData?.data?.total_votes;
      const pollVotesIncreased = currentTotalVotes > totalVotes;

      if (pollVotesIncreased) {
        setAssignedPoll(pollData);
        totalTimeSinceLastResult = 0;
      } else {
        totalTimeSinceLastResult += interval;
      }

      totalVotes = currentTotalVotes;
      totalTimeSinceInitialRequest += interval;

      const newInterval = calculateNewPollingInterval({
        interval,
        totalTimeSinceLastResult,
        initialInterval,
        allowedTotalDuration,
        durationBeforeIntervalIncrease,
      });
      const newTotalTime = totalTimeSinceResultIncrease + interval;
      recursiveTimeout(newInterval, newTotalTime);
    }, interval);

    pendingTimeouts.push(timeout);
  };

  // Check whether the user is on the current window and tab
  useEffect(() => {
    const onVisibilityIn = (isVisible) => {
      setDocumentIsVisibile(isVisible);
    };
    document.addEventListener("visibilitychange", () => {
      onVisibilityIn(document.visibilityState === "visible");
    });

    window.addEventListener("focus", () => {
      onVisibilityIn(true);
    });

    window.addEventListener("blur", () => {
      onVisibilityIn(false);
    });

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityIn);
      document.removeEventListener("blur", onVisibilityIn);
      document.removeEventListener("focus", onVisibilityIn);
    };
  }, []);

  useEffect(() => {
    const shouldStartPolling =
      isInView &&
      documentIsVisibile &&
      ((isTeacher && !!pollId && isDraftAssignment === false) ||
        (isStudent && !!pollId && canSeeResults)) &&
      !isUsingSocketConnection;

    if (shouldStartPolling) {
      recursiveTimeout(initialInterval);
    }

    return () => {
      pendingTimeouts.forEach((pendingTimeout) => clearTimeout(pendingTimeout));
    };
  }, [
    canSeeResults,
    documentIsVisibile,
    isDraftAssignment,
    isInView,
    isStudent,
    isTeacher,
    pollId,
    totalPollAssignees,
    isUsingSocketConnection,
  ]);
};

export default usePollingForPollResults;
